import React, { FormEvent, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Code2,
  Database,
  ExternalLink,
  FileSpreadsheet,
  KeyRound,
  Play,
  RefreshCcw,
  Send,
  ShieldCheck,
  UploadCloud,
} from 'lucide-react';
import { DryRunSheetsClient } from './clients/googleSheetsClient';
import { RoseRocketMockClient } from './clients/roseRocketMockClient';
import { loadConfig } from './config/env';
import orders from './mock-data/orders.json';
import { runSync } from './sync/syncRunner';
import type { RoseRocketOrder, SyncSummary } from './types/entities';
import { SHEET_HEADERS } from './types/sheets';
import './styles.css';

const demoOrders = orders as RoseRocketOrder[];
const RESULT_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1XXNwJx3mbuB8jPrQI5RG-zvFH5xWOmrIZIboPjNgKpg/edit?usp=sharing';
const publicSheetUrl = RESULT_SHEET_URL;

const pipeline = [
  { icon: Database, label: 'Mock Rose Rocket dataset', detail: 'The orders shown on this page are the source dataset for the demo import.' },
  { icon: ShieldCheck, label: 'Validation + mapping', detail: 'Order fields are normalized into Google Sheets columns before writing.' },
  { icon: UploadCloud, label: 'Import endpoint', detail: 'The frontend calls /api/import-mock-orders through a Cloudflare Pages Function.' },
  { icon: FileSpreadsheet, label: 'Live Google Sheet', detail: 'Rows appear in the “Rose Rocket Orders Import” tab of the public result sheet.' },
];

const mappingRows = [
  ['Order ID', 'id', 'rr_order_id', 'Unique key for updates'],
  ['Order Number', 'order_number', 'order_number', 'Visible business reference'],
  ['Status', 'status', 'status', 'Operational state'],
  ['Customer', 'customer_name', 'customer_name', 'Readable account name'],
  ['Pickup', 'pickup_location.*', 'pickup_city / pickup_state', 'Flattened for Sheets'],
  ['Delivery', 'delivery_location.*', 'delivery_city / delivery_state', 'Flattened for Sheets'],
  ['Rate', 'rate + currency', 'rate / currency', 'Numeric value preserved'],
  ['Updated At', 'updated_at', 'rr_updated_at', 'Used for sync visibility'],
];

const liveRequirements = [
  'Rose Rocket Developer Portal access',
  'API credentials / OAuth app details',
  'Allowed scopes and object permissions',
  'Real sample payloads from their account',
  'Final list of entities to sync',
  'Google Sheet owner and access model',
];

type SubmitState =
  | { status: 'idle'; message: string }
  | { status: 'success'; message: string }
  | { status: 'error'; message: string };

type ImportState =
  | { status: 'idle'; message: string }
  | { status: 'success'; message: string }
  | { status: 'error'; message: string };

function formatMoney(value?: number, currency = 'USD') {
  if (typeof value !== 'number') return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

function statusClass(status: string) {
  if (status === 'delivered') return 'good';
  if (status === 'cancelled') return 'bad';
  if (status === 'in_transit' || status === 'dispatched') return 'active';
  return 'neutral';
}

function Metric({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong>{hint ? <small>{hint}</small> : null}</div>;
}

function App() {
  const [summaries, setSummaries] = useState<SyncSummary[]>([]);
  const [isRunningSimulation, setIsRunningSimulation] = useState(false);
  const [runCount, setRunCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importAccessCode, setImportAccessCode] = useState('DEMO2026');
  const [submitState, setSubmitState] = useState<SubmitState>({
    status: 'idle',
    message: 'Submit a single mock order, then open the live Google Sheet to verify the appended row.',
  });
  const [importState, setImportState] = useState<ImportState>({
    status: 'idle',
    message: 'Import the visible mock Rose Rocket orders into the live Google Sheet.',
  });

  const totals = useMemo(() => summaries.reduce((acc, item) => {
    acc.fetched += item.fetchedCount;
    acc.created += item.createdCount;
    acc.updated += item.updatedCount;
    acc.skipped += item.skippedCount;
    acc.failed += item.failedCount;
    return acc;
  }, { fetched: 0, created: 0, updated: 0, skipped: 0, failed: 0 }), [summaries]);

  const fallbackRows: Partial<SyncSummary>[] = [
    { entity: 'orders', status: 'success', fetchedCount: 5, createdCount: 5, updatedCount: 0, skippedCount: 0, failedCount: 0, message: 'Simulation only: five order rows would be created.' },
    { entity: 'customers', status: 'success', fetchedCount: 4, createdCount: 4, updatedCount: 0, skippedCount: 0, failedCount: 0, message: 'Simulation only: customer rows would be mapped.' },
    { entity: 'invoices', status: 'success', fetchedCount: 3, createdCount: 3, updatedCount: 0, skippedCount: 0, failedCount: 0, message: 'Simulation only: invoice rows would be mapped.' },
  ];

  async function runLocalSimulation() {
    setIsRunningSimulation(true);
    const sheets = new DryRunSheetsClient();
    const config = loadConfig({ SYNC_ENTITIES: 'orders,customers,invoices', SYNC_MODE: 'mock', LOG_LEVEL: 'info' });
    const first = await runSync({ config, roseRocket: new RoseRocketMockClient(), sheets, dryRun: false });
    const result = runCount % 2 === 0 ? first : await runSync({ config, roseRocket: new RoseRocketMockClient(), sheets, dryRun: false });
    setSummaries(result);
    setRunCount((value) => value + 1);
    setIsRunningSimulation(false);
  }

  async function importMockOrders() {
    setIsImporting(true);
    setImportState({ status: 'idle', message: 'Importing mock Rose Rocket orders to Google Sheets…' });

    try {
      const response = await fetch('/api/import-mock-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessCode: importAccessCode }),
      });
      const data = await response.json() as {
        ok: boolean;
        message?: string;
        error?: string;
        importedCount?: number;
        tab?: string;
        updatedRange?: string;
      };
      if (!response.ok || !data.ok) throw new Error(data.error || 'Import failed');
      setImportState({
        status: 'success',
        message: `${data.message || 'Imported mock orders'}${data.tab ? ` into “${data.tab}”` : ''}. Open the live Sheet to verify the rows.`,
      });
    } catch (error) {
      setImportState({ status: 'error', message: error instanceof Error ? error.message : 'Import failed' });
    } finally {
      setIsImporting(false);
    }
  }

  async function submitOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    setIsSubmitting(true);
    setSubmitState({ status: 'idle', message: 'Sending order to Google Sheets…' });

    try {
      const response = await fetch('/api/submit-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json() as { ok: boolean; message?: string; error?: string; updatedRange?: string };
      if (!response.ok || !data.ok) throw new Error(data.error || 'Submit failed');
      setSubmitState({
        status: 'success',
        message: `${data.message || 'Submitted'}${data.updatedRange ? ` (${data.updatedRange})` : ''}. Open the live result sheet to verify it.`,
      });
      form.reset();
    } catch (error) {
      setSubmitState({ status: 'error', message: error instanceof Error ? error.message : 'Submit failed' });
    } finally {
      setIsSubmitting(false);
    }
  }

  const visibleSummaries = summaries.length ? summaries : fallbackRows;
  const shownTotals = summaries.length ? totals : { fetched: 12, created: 12, updated: 0, skipped: 0, failed: 0 };
  const samplePayload = JSON.stringify(demoOrders[0], null, 2);

  return (
    <main>
      <section className="hero">
        <div className="hero-copy">
          <div className="topline">
            <span className="badge"><CheckCircle2 size={16} /> Working import demo</span>
            <span className="badge muted"><AlertTriangle size={16} /> Rose Rocket API is mocked</span>
            <span className="badge blue"><FileSpreadsheet size={16} /> Writes to live Google Sheet</span>
          </div>
          <h1>Rose Rocket → Google Sheets import prototype</h1>
          <p className="lead">The mock Rose Rocket orders are not just displayed on the page. You can import the visible dataset into a live Google Sheet and verify the rows immediately.</p>
          <div className="hero-actions">
            <a className="primary-link" href="#orders"><UploadCloud size={17} /> Import visible mock orders</a>
            <a href={publicSheetUrl} target="_blank" rel="noreferrer"><ExternalLink size={17} /> Open live result Sheet</a>
          </div>
        </div>

        <aside className="control-card result-card">
          <div className="card-title"><FileSpreadsheet size={22} /><div><strong>Live result sheet</strong><span>Imported rows appear in the “Rose Rocket Orders Import” tab.</span></div></div>
          <a className="big-sheet-link" href={publicSheetUrl} target="_blank" rel="noreferrer">
            <span>Open Google Sheet</span>
            <ExternalLink size={20} />
          </a>
          <p className="sheet-note">Main proof: import the mock orders, then open this sheet and check the <strong>Rose Rocket Orders Import</strong> tab.</p>
          <div className="metrics-grid mini">
            <Metric label="Output" value="Live" hint="Google Sheet" />
            <Metric label="Import API" value="POST" hint="/api/import-mock-orders" />
          </div>
        </aside>
      </section>

      <section id="orders" className="panel import-panel">
        <div className="section-title">
          <div><p className="eyebrow">Mock Rose Rocket dataset</p><h2>Import these orders to Google Sheets</h2></div>
          <a className="run-chip link-chip" href={publicSheetUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Open live result sheet</a>
        </div>
        <div className="import-callout">
          <UploadCloud size={22} />
          <div>
            <strong>This is the main demo action.</strong>
            <span>The 5 visible mock Rose Rocket orders below will be written into the live Google Sheet tab “Rose Rocket Orders Import”.</span>
          </div>
          <div className="import-actions">
            <input value={importAccessCode} onChange={(event) => setImportAccessCode(event.target.value)} placeholder="Demo access code" />
            <button type="button" onClick={importMockOrders} disabled={isImporting}>
              <UploadCloud size={18} /> {isImporting ? 'Importing…' : 'Import mock orders'}
            </button>
          </div>
        </div>
        <p className={`submit-message ${importState.status}`}>{importState.message}</p>
        <div className="table-wrap"><table><thead><tr><th>Order</th><th>Status</th><th>Customer</th><th>Lane</th><th>Dates</th><th>Carrier</th><th>Equipment</th><th>Rate</th></tr></thead><tbody>{demoOrders.map((order) => <tr key={order.id}><td><span className="strong">{order.order_number}</span><small>{order.id}</small></td><td><span className={`pill ${statusClass(order.status)}`}>{order.status.replace('_', ' ')}</span></td><td>{order.customer_name}</td><td>{order.pickup_location?.city}, {order.pickup_location?.state} <ArrowRight size={13} /> {order.delivery_location?.city}, {order.delivery_location?.state}</td><td>{order.pickup_date} → {order.delivery_date}</td><td>{order.carrier_name}</td><td>{order.equipment_type}</td><td>{formatMoney(order.rate, order.currency)}</td></tr>)}</tbody></table></div>
      </section>

      <section id="submit" className="panel submit-panel secondary-panel">
        <div className="section-title">
          <div><p className="eyebrow">Optional single-row test</p><h2>Submit one custom mock order</h2></div>
          <a className="run-chip link-chip" href={publicSheetUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Open live result sheet</a>
        </div>
        <div className="result-callout muted-callout">
          <FileSpreadsheet size={20} />
          <div>
            <strong>Secondary test.</strong>
            <span>This appends one manually entered row to the “Demo Submissions” tab.</span>
          </div>
          <a href={publicSheetUrl} target="_blank" rel="noreferrer">View result <ExternalLink size={15} /></a>
        </div>
        <form className="order-form" onSubmit={submitOrder}>
          <input name="website" className="hidden-field" tabIndex={-1} autoComplete="off" />
          <label><span>Demo access code</span><input name="accessCode" placeholder="Optional if not required by server" defaultValue="DEMO2026" /></label>
          <label><span>Order number</span><input name="orderNumber" defaultValue={`ORD-DEMO-${Math.floor(Date.now() / 1000).toString().slice(-5)}`} required /></label>
          <label><span>Customer</span><input name="customerName" defaultValue="Demo Freight Customer" required /></label>
          <label><span>Status</span><select name="status" defaultValue="booked"><option value="booked">booked</option><option value="dispatched">dispatched</option><option value="in_transit">in_transit</option><option value="delivered">delivered</option><option value="cancelled">cancelled</option></select></label>
          <label><span>Pickup city</span><input name="pickupCity" defaultValue="Chicago" required /></label>
          <label><span>Pickup state</span><input name="pickupState" defaultValue="IL" /></label>
          <label><span>Delivery city</span><input name="deliveryCity" defaultValue="Columbus" required /></label>
          <label><span>Delivery state</span><input name="deliveryState" defaultValue="OH" /></label>
          <label><span>Carrier</span><input name="carrierName" defaultValue="Demo Carrier LLC" /></label>
          <label><span>Equipment</span><select name="equipmentType" defaultValue="Dry Van"><option>Dry Van</option><option>Reefer</option><option>Flatbed</option><option>Box Truck</option></select></label>
          <label><span>Rate</span><input name="rate" type="number" min="0" step="1" defaultValue="1850" /></label>
          <label><span>Currency</span><input name="currency" defaultValue="USD" maxLength={3} /></label>
          <label><span>Reference</span><input name="referenceNumber" defaultValue="WEB-DEMO" /></label>
          <label className="wide"><span>Notes</span><input name="notes" placeholder="Optional note shown in the demo sheet" /></label>
          <button type="submit" disabled={isSubmitting}><Send size={18} /> {isSubmitting ? 'Submitting…' : 'Send one row'}</button>
        </form>
        <p className={`submit-message ${submitState.status}`}>{submitState.message}</p>
      </section>

      <section className="panel summary-panel secondary-panel">
        <div className="section-title"><div><p className="eyebrow">Local simulation only</p><h2>Sync engine preview</h2></div><button className="secondary-button" onClick={runLocalSimulation} disabled={isRunningSimulation}><Play size={18} /> {isRunningSimulation ? 'Running…' : 'Run local simulation'}</button></div>
        <p className="section-note">This part does not write to Google Sheets. It only previews create/update/skip logic. The real write is the import action above.</p>
        <div className="table-wrap"><table><thead><tr><th>Entity</th><th>Status</th><th>Fetched</th><th>Created</th><th>Updated</th><th>Skipped</th><th>Failed</th><th>Message</th></tr></thead><tbody>{visibleSummaries.map((row) => <tr key={row.entity}><td className="strong">{row.entity}</td><td><span className="pill good">{row.status}</span></td><td>{row.fetchedCount}</td><td>{row.createdCount}</td><td>{row.updatedCount}</td><td>{row.skippedCount}</td><td>{row.failedCount}</td><td>{row.message}</td></tr>)}</tbody></table></div>
      </section>

      <section className="pipeline-grid">{pipeline.map((step, index) => { const Icon = step.icon; return <article className="pipeline-card" key={step.label}><span className="step">0{index + 1}</span><Icon size={24} /><h3>{step.label}</h3><p>{step.detail}</p></article>; })}</section>

      <section className="split">
        <div className="panel compact"><p className="eyebrow">Field mapping</p><h2>Rose Rocket-like payload → Sheet columns</h2><div className="mapping-list">{mappingRows.map(([label, source, target, note]) => <div className="mapping-row" key={label}><strong>{label}</strong><code>{source}</code><ArrowRight size={15} /><code>{target}</code><span>{note}</span></div>)}</div></div>
        <div className="panel compact dark"><p className="eyebrow">Sample source payload</p><h2>Mock order JSON</h2><pre>{samplePayload}</pre></div>
      </section>

      <section className="split">
        <div className="panel compact"><p className="eyebrow">Sheets schema</p><h2>Tabs prepared for output</h2>{Object.entries(SHEET_HEADERS).map(([sheet, headers]) => <div className="schema" key={sheet}><strong>{sheet}</strong><span>{headers.slice(0, 9).join(' · ')}{headers.length > 9 ? ' · …' : ''}</span></div>)}</div>
        <div className="panel compact"><p className="eyebrow">What is still needed</p><h2>For live Rose Rocket integration</h2><ul className="requirement-list">{liveRequirements.map((item) => <li key={item}><KeyRound size={16} />{item}</li>)}</ul></div>
      </section>

      <section className="panel delivery"><div><p className="eyebrow">Honest boundary</p><h2>This proves import flow and real Sheets write, not live Rose Rocket compatibility yet.</h2><p>The prototype is intentionally built around mock Rose Rocket data. Once the client provides Rose Rocket API access and real sample records, the mock source can be replaced by a live API client while keeping the mapping, validation, and Google Sheets output flow.</p></div><div className="proofs"><span><Code2 size={17} /> API source is replaceable</span><span><UploadCloud size={17} /> Mock orders import to Sheets</span><span><RefreshCcw size={17} /> Local sync preview available</span><span><FileSpreadsheet size={17} /> Real Google Sheets output</span></div></section>
    </main>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
